// CloudHeist - AWS Cloud Practitioner
// Histoire : Une intrusion detectee dans le cloud. Le CISO mene l'enquete.

import type {
  Character,
  ScenarioChapter,
  SequenceItem,
  StoryContent,
  ChatContent,
  CardContent,
  QuizContent,
  TaskContent,
} from './storyLoader';

export interface CloudHeistScenario {
  id: string;
  title: string;
  subtitle: string;
  certification: string;
  coverImage: ReturnType<typeof require>;
  characters: Character[];
  chapters: ScenarioChapter[];
}

const cloudHeistCharacters: Character[] = [
  { id: 'keiko',   name: 'Keiko',   role: 'Ethical Hacker',         color: '#065F46', avatar: 'keiko'   },
  { id: 'nabil',   name: 'Nabil',   role: 'CISO',                   color: '#7C2D12', avatar: 'nabil'   },
  { id: 'leila',   name: 'Leila',   role: 'DevOps Lead',            color: '#4338CA', avatar: 'leila'   },
  { id: 'yasmine', name: 'Yasmine', role: 'Cloud Security Analyst',  color: '#701A75', avatar: 'yasmine' },
];

// ── Chapitre I : L'Intrusion ──────────────────────────────────────────────────

const scene1: SequenceItem = {
  type: 'story',
  id: 'ch-scene-1',
  title: "Scene 1 : Alerte CloudTrail",
  duration: 4,
  content: {
    narrativeText:
      "03h47 du matin. L'alerte sonne dans le SOC.\n\n" +
      "**Yasmine :** \"Nabil, acces non autorise detecte. Compte IAM 'dev-ci-runner' a assume le role AdminAccess depuis une IP inattendue : 185.220.101.x — un noeud Tor.\"\n\n" +
      "**Nabil :** \"Quand ?\"\n\n" +
      "**Yasmine :** \"Il y a 23 minutes. Il a liste tous les buckets S3, decrit les instances EC2, et tente d'acceder aux secrets dans Secrets Manager.\"\n\n" +
      "**Nabil :** \"Ce compte n'aurait jamais du avoir ces permissions. Keiko, c'est une attaque de type 'privilege escalation' ou acces direct ?\"\n\n" +
      "**Keiko :** \"Acces direct. La cle d'acces AWS a ete exposee quelque part. GitHub probablement. Ces bots scannent les repos en temps reel.\"\n\n" +
      "**Nabil :** \"Leila, c'est votre pipeline CI/CD. Vous avez des credentials hardcodes ?\"\n\n" +
      "**Leila :** \"Non... mais le .env du runner est peut-etre commite par erreur dans une branche ancienne.\"\n\n" +
      "**Nabil :** \"Revocation immediate de la cle. Yasmine, bloquez l'IP. Keiko, analysez les logs CloudTrail des 48 dernieres heures.\"\n\n" +
      "**Keiko :** \"Sur quoi on cherche en priorite ?\"\n\n" +
      "**Nabil :** \"Toute action sur S3, IAM et Secrets Manager. Et les CreateUser ou AttachUserPolicy — ils cherchent a persister.\"\n\n" +
      "**Yasmine :** \"Cle revoquee. Acces bloque. L'intrus est dehors... pour l'instant.\"",
    characters: ['nabil', 'keiko', 'leila', 'yasmine'],
  } as StoryContent,
};

const card1: SequenceItem = {
  type: 'card',
  id: 'ch-card-1',
  title: 'Concept : Modele de responsabilite partagee AWS',
  duration: 5,
  content: {
    title: 'AWS Shared Responsibility Model',
    body:
      "## Modele de responsabilite partagee AWS\n\n" +
      "AWS et le client partagent la responsabilite de la securite. La frontiere depend du service.\n\n" +
      "### AWS est responsable de :\n" +
      "- **Securite DU cloud** : data centers, hardware, reseau physique, hyperviseurs\n" +
      "- Infrastructure globale : Regions, Availability Zones, Edge Locations\n" +
      "- Services managés : la securite de RDS, Lambda, S3 (le service lui-meme)\n\n" +
      "### Le CLIENT est responsable de :\n" +
      "- **Securite DANS le cloud** : configuration, donnees, acces\n" +
      "- Gestion des identites IAM et des permissions\n" +
      "- Chiffrement des donnees au repos et en transit\n" +
      "- Configuration des Security Groups et NACLs\n" +
      "- Patching des OS sur les instances EC2\n\n" +
      "### Exemples pratiques\n" +
      "| Scenario | Responsabilite |\n" +
      "|---|---|\n" +
      "| Cle IAM exposee sur GitHub | CLIENT |\n" +
      "| Panne d'un data center AWS | AWS |\n" +
      "| Bucket S3 public non voulu | CLIENT |\n" +
      "| Vulnerabilite dans Lambda runtime | AWS |\n\n" +
      "### Principe du moindre privilege\n" +
      "Chaque entite (user, role, service) ne doit avoir que les permissions strictement necessaires a sa fonction.",
  } as CardContent,
};

const chat1: SequenceItem = {
  type: 'chat',
  id: 'ch-chat-1',
  title: 'Discussion CISO : Vecteurs d attaque',
  duration: 6,
  content: {
    tutorCharacterId: 'nabil',
    messages: [
      {
        id: 'ch-chat-1-q1',
        sender: 'character',
        characterId: 'nabil',
        text: "La cle IAM a ete exposee sur GitHub. Quelle est la MEILLEURE pratique pour eviter ca a l'avenir ?",
        type: 'single-choice',
        options: [
          "Utiliser des variables d'environnement dans le code",
          "Utiliser IAM Roles au lieu de cles d'acces longues pour les services AWS",
          "Chiffrer les cles avant de les commiter",
          "Changer les cles tous les 30 jours",
        ],
        correctAnswer: "Utiliser IAM Roles au lieu de cles d'acces longues pour les services AWS",
        explanation: "Les IAM Roles avec des credentials temporaires (via STS AssumeRole) sont la meilleure pratique. Une instance EC2 ou un Lambda n'a pas besoin de cle d'acces — il assume un role. Pas de cle = pas de fuite possible.",
      },
      {
        id: 'ch-chat-1-q2',
        sender: 'character',
        characterId: 'nabil',
        text: "Quels services AWS auraient pu detecter ou prevenir cette intrusion plus tot ?",
        type: 'multiple-choice',
        options: [
          "AWS CloudTrail — journalisation de toutes les API calls",
          "AWS GuardDuty — detection de menaces basee sur ML",
          "AWS Config — conformite des configurations",
          "AWS Shield — protection DDoS",
        ],
        correctAnswer: [
          "AWS CloudTrail — journalisation de toutes les API calls",
          "AWS GuardDuty — detection de menaces basee sur ML",
        ],
        explanation: "CloudTrail enregistre toutes les API calls et aurait permis une detection plus rapide. GuardDuty analyse les logs CloudTrail, VPC Flow Logs et DNS logs pour detecter les comportements suspects via ML.",
      },
      {
        id: 'ch-chat-1-q3',
        sender: 'character',
        characterId: 'nabil',
        text: "Qu'est-ce que le principe de 'least privilege' implique pour un compte de service CI/CD ?",
        type: 'single-choice',
        options: [
          "Le compte peut avoir des droits admin pour deployer facilement",
          "Le compte n'a que les permissions pour deployer les ressources specifiques de son pipeline",
          "Le compte n'a aucun droit par defaut",
          "Le compte peut tout lire mais ne peut rien ecrire",
        ],
        correctAnswer: "Le compte n'a que les permissions pour deployer les ressources specifiques de son pipeline",
        explanation: "Least privilege = permissions minimales necessaires pour fonctionner. Un CI/CD runner qui deploie sur Lambda n'a pas besoin d'acceder a S3, IAM ou Secrets Manager. Les permissions doivent etre specifiques aux ressources et actions requises.",
      },
    ],
  } as ChatContent,
};

const task1: SequenceItem = {
  type: 'task',
  id: 'ch-task-1',
  title: 'Analyser les logs CloudTrail pour identifier la source',
  duration: 25,
  content: {
    platform: 'AWS Console / AWS CLI',
    objective: "Analyser les logs CloudTrail pour reconstituer la timeline de l'intrusion",
    azurePortalPath: 'console.aws.amazon.com > CloudTrail > Event history',
    steps: [
      {
        stepNumber: 1,
        action: "Acceder a CloudTrail",
        detail: "Console AWS > CloudTrail > Event history. Filtrer par 'User name' = 'dev-ci-runner'. Plage : 48 dernières heures.",
      },
      {
        stepNumber: 2,
        action: "Identifier les evenements suspects",
        detail: "Filtrer par Event source: 's3.amazonaws.com', 'iam.amazonaws.com', 'secretsmanager.amazonaws.com'. Noter l'heure, l'IP source et les ressources accedees.",
      },
      {
        stepNumber: 3,
        action: "Analyser avec Athena",
        detail: "Creer une table Athena sur les logs CloudTrail S3. SQL: SELECT eventTime, eventName, sourceIPAddress, requestParameters FROM cloudtrail WHERE userIdentity.userName = 'dev-ci-runner' ORDER BY eventTime DESC LIMIT 100;",
      },
      {
        stepNumber: 4,
        action: "Verifier les tentatives de persistance",
        detail: "Rechercher: CreateUser, AttachUserPolicy, CreateAccessKey, PutRolePolicy dans les evenements. Ces actions indiquent une tentative de creer une backdoor.",
      },
      {
        stepNumber: 5,
        action: "Documenter la timeline",
        detail: "Creer un rapport chronologique : 1re connexion, ressources accedees, tentatives de persistance, heure de revocation de la cle.",
      },
    ],
    validation: "Timeline complete documentee. Rapport soumis a Nabil avec le vecteur d'attaque identifie.",
  } as TaskContent,
};

const quiz1: SequenceItem = {
  type: 'quiz',
  id: 'ch-quiz-1',
  title: 'Quiz : AWS Security et IAM',
  duration: 5,
  content: {
    questions: [
      {
        id: 'ch-q1-1',
        question: "Quelle est la MEILLEURE pratique IAM pour les applications AWS (EC2, Lambda) ?",
        type: 'single-choice',
        options: [
          "Hardcoder les cles dans le code source",
          "Stocker les cles dans des variables d'environnement",
          "Utiliser des IAM Roles avec des credentials temporaires STS",
          "Partager un compte admin entre les developpeurs",
        ],
        correctAnswer: "Utiliser des IAM Roles avec des credentials temporaires STS",
        explanation: "Les IAM Roles via STS AssumeRole generent des credentials temporaires (15min-12h). Pas de cle longue duree = pas de fuite possible dans le code source ou les environnements.",
      },
      {
        id: 'ch-q1-2',
        question: "Qu'est-ce que le MFA (Multi-Factor Authentication) protege specifiquement contre ?",
        type: 'single-choice',
        options: [
          "Les attaques DDoS",
          "Les injections SQL",
          "Le vol de credentials (password seul ne suffit pas)",
          "Les vulnerabilites logicielles",
        ],
        correctAnswer: "Le vol de credentials (password seul ne suffit pas)",
        explanation: "Le MFA ajoute un second facteur (TOTP, hardware token) qui rend un mot de passe vole inutile seul. Meme si un attaquant obtient votre password, il ne peut pas se connecter sans le second facteur.",
      },
      {
        id: 'ch-q1-3',
        question: "Dans le modele de responsabilite partagee AWS, qui est responsable du patching de l'OS sur une instance EC2 ?",
        type: 'single-choice',
        options: [
          "AWS uniquement",
          "Le client uniquement",
          "Responsabilite partagee 50/50",
          "Depende du type d'instance",
        ],
        correctAnswer: "Le client uniquement",
        explanation: "Sur EC2, le client controle l'OS. AWS gere uniquement l'hyperviseur et le hardware. Le patching de l'OS (Windows, Linux), les security groups et la configuration des applications sont la responsabilite du client.",
      },
    ],
  } as QuizContent,
};

const scene2: SequenceItem = {
  type: 'story',
  id: 'ch-scene-2',
  title: "Scene 2 : L'insider suspect",
  duration: 4,
  content: {
    narrativeText:
      "09h15. La salle de crise. Keiko a analyse les logs.\n\n" +
      "**Keiko :** \"J'ai la timeline complete. La cle a ete exposee dans un commit GitHub il y a 3 semaines. Branche 'feature/ci-test', auteur : dev-account-07.\"\n\n" +
      "**Nabil :** \"Dev-account-07. C'est un stagiaire qui a quitte la societe il y a 10 jours. Son compte est toujours actif.\"\n\n" +
      "**Leila :** \"C'est pas possible. On a un process de deprovisioning.\"\n\n" +
      "**Nabil :** \"Le process existe, mais il n'a pas ete suivi. Yasmine, combien d'anciens employes ont des comptes actifs ?\"\n\n" +
      "**Yasmine :** \"Je lance l'audit. ... 23 comptes. Dont 8 avec des permissions elevees.\"\n\n" +
      "**Nabil :** \"Revocation immediate des 23 comptes. Et on met en place une politique : deprovisioning automatique via Entra ID/IAM le jour du depart.\"\n\n" +
      "**Keiko :** \"Et l'attaquant externe a simplement trouve la cle sur GitHub. Il n'est pas lie a l'ex-stagiaire probablement.\"\n\n" +
      "**Nabil :** \"La responsabilite reste. Principe du least privilege : ce compte n'aurait jamais du avoir acces a AdminAccess.\"\n\n" +
      "**Leila :** \"Je corrige la policy IAM du pipeline maintenant. Permissions minimales pour le CI/CD uniquement.\"",
    characters: ['nabil', 'keiko', 'leila', 'yasmine'],
  } as StoryContent,
};

const card2: SequenceItem = {
  type: 'card',
  id: 'ch-card-2',
  title: 'Concept : IAM Policies et Least Privilege',
  duration: 4,
  content: {
    title: 'AWS IAM Policies : Principe du moindre privilege',
    body:
      "## AWS IAM Policies\n\n" +
      "Une policy IAM definit ce qu'une entite (user, role, group) peut ou ne peut pas faire.\n\n" +
      "### Structure d'une policy\n" +
      "```json\n" +
      "{\n" +
      "  \"Version\": \"2012-10-17\",\n" +
      "  \"Statement\": [{\n" +
      "    \"Effect\": \"Allow\",\n" +
      "    \"Action\": [\"s3:GetObject\", \"s3:PutObject\"],\n" +
      "    \"Resource\": \"arn:aws:s3:::my-bucket/*\"\n" +
      "  }]\n" +
      "}\n" +
      "```\n\n" +
      "### Types de policies\n" +
      "- **Managed policies** : AWS-managed (ex: AmazonS3ReadOnlyAccess) ou customer-managed\n" +
      "- **Inline policies** : attachees directement a une entite (deconseille)\n" +
      "- **Resource policies** : attachees a la ressource (ex: S3 Bucket Policy)\n\n" +
      "### Regles de securite IAM\n" +
      "1. Ne jamais utiliser le compte root pour les operations courantes\n" +
      "2. Activer le MFA sur tous les comptes privilegies\n" +
      "3. Utiliser des roles pour les applications, pas des cles longues\n" +
      "4. Appliquer le principe du moindre privilege\n" +
      "5. Auditer regulierement avec IAM Access Analyzer\n" +
      "6. Deprovisioner immediatement les comptes d'anciens employes\n\n" +
      "### IAM Access Analyzer\n" +
      "Identifie automatiquement les ressources partagees publiquement ou avec des comptes externes.",
  } as CardContent,
};

const task2: SequenceItem = {
  type: 'task',
  id: 'ch-task-2',
  title: 'Restreindre les permissions avec une policy IAM custom',
  duration: 25,
  content: {
    platform: 'AWS Console / AWS CLI',
    objective: "Creer une policy IAM minimale pour le compte de service CI/CD",
    azurePortalPath: 'console.aws.amazon.com > IAM > Policies > Create policy',
    steps: [
      {
        stepNumber: 1,
        action: "Analyser les permissions necessaires",
        detail: "Le pipeline CI/CD doit uniquement : deployer sur Lambda, lire depuis S3 (artifacts), ecrire dans CloudWatch Logs. Rien de plus.",
      },
      {
        stepNumber: 2,
        action: "Creer la policy via la console",
        detail: "IAM > Policies > Create policy > JSON. Definir Allow uniquement pour lambda:UpdateFunctionCode, s3:GetObject sur le bucket d'artifacts, logs:CreateLogGroup.",
      },
      {
        stepNumber: 3,
        action: "Tester avec IAM Policy Simulator",
        detail: "IAM > Policy Simulator. Simuler les actions avec la nouvelle policy. Verifier que AdminAccess est bien refuse.",
      },
      {
        stepNumber: 4,
        action: "Migrer vers IAM Role",
        detail: "Supprimer les cles d'acces longues. Creer un IAM Role 'ci-cd-deploy-role' avec la policy minimale. Configurer le pipeline pour assumer ce role via STS.",
      },
    ],
    validation: "Policy minimale en place. Cles longues revoquees. Pipeline fonctionne avec le role. IAM Access Analyzer : 0 finding public.",
  } as TaskContent,
};

const chapter1: ScenarioChapter = {
  id: 'chapter-1',
  title: "L'Intrusion",
  subtitle: 'AWS Cloud Practitioner — Securite et responsabilite partagee',
  learningObjectives: [
    "Comprendre le modele de responsabilite partagee AWS",
    "Analyser les logs CloudTrail pour detecter une intrusion",
    "Appliquer le principe du moindre privilege avec IAM",
    "Configurer le MFA et les bonnes pratiques IAM",
    "Creer des policies IAM minimales pour les comptes de service",
  ],
  sequence: [scene1, card1, chat1, task1, quiz1, scene2, card2, task2],
};

export const cloudHeistScenario: CloudHeistScenario = {
  id: 'cloud-heist',
  title: 'Cloud Heist',
  subtitle: "Qui a compromis notre cloud AWS ?",
  certification: 'AWS Cloud Practitioner',
  coverImage: require('../../../assets/images/characters/nina_authority.png'),
  characters: cloudHeistCharacters,
  chapters: [chapter1],
};

export default cloudHeistScenario;
